import { useState, useCallback, useEffect } from 'react';
import { useAuth } from '@/components/auth/SupabaseAuthProvider';

interface SubscriptionDetails {
  isActive: boolean;
  plan: string | null;
  status: string | null;
  trial_start: number | null;
  trial_end: number | null;
  current_period_end: number | null;
  cancel_at_period_end: boolean;
}

export function useSubscription() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [subscriptionDetails, setSubscriptionDetails] = useState<SubscriptionDetails | null>(null);

  const refreshStatus = useCallback(async () => {
    if (!user?.id || !user?.email) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      console.log('Fetching subscription for email:', user.email);

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-subscription-status`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
          },
          body: JSON.stringify({ userId: user.id, email: user.email })
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Subscription API error:', errorText);
        throw new Error('Failed to fetch subscription status');
      }

      const data = await response.json();
      console.log('Subscription data:', data);
      
      if (data.subscription) {
        const sub = data.subscription;
        setSubscriptionDetails({
          isActive: ['active', 'trialing'].includes(sub.status),
          plan: sub.plan?.name || 'Basic Subscription',
          status: sub.status,
          trial_start: sub.trial_start,
          trial_end: sub.trial_end,
          current_period_end: sub.current_period_end,
          cancel_at_period_end: sub.cancel_at_period_end
        });
      } else {
        setSubscriptionDetails(null);
      }
    } catch (err) {
      console.error('Error fetching subscription:', err);
      setError(err as Error);
      setSubscriptionDetails(null);
    } finally {
      setLoading(false);
    }
  }, [user]); 

  useEffect(() => {
    if (user?.id && user?.email) {
      refreshStatus();
    }
  }, [user?.id, user?.email, refreshStatus]);

  return { subscriptionDetails, loading, error, refreshStatus };
}